import { GvAngebotData, GvPerson, GvPersonenData, GvRabatt, GvTarif } from '../../gvangebot.model.types';
import { createValidationResult, IValidationResult } from '@mng/model';

export enum GvRisikoType {
  NEU = 'NEU',
  BESTAND = 'BESTAND',
  BESTAND_GEAENDERT = 'BESTAND_GEAENDERT',
  STORNIERT = 'STORNIERT'
}

export const RABATT_FZ = 'Gruppenvertragsrabatt';
export const RABATT_FAMILIENBONUS = 'Familienbonus';

export class GvBerechnungMessageState {
  static ERROR = 'E';
  static INFO = 'I';
  static WARNING = 'W';
  static NICHT_KOMBINIERBAR = 'K';
  static WEITERE_TARIFE_ERFORDERLICH = 'M'; // Multiple
}

export function getGvFzRabatt(tarif: GvTarif) {
  return getGvRabatt(tarif, RABATT_FZ);
}

export function getGvFamilienbonusRabatt(tarif: GvTarif) {
  return getGvRabatt(tarif, RABATT_FAMILIENBONUS);
}

function getGvRabatt(tarif: GvTarif, rabattArt: string): number {
  if (tarif.rabatte && tarif.rabatte.length > 0) {
    const gvRabatt: GvRabatt = tarif.rabatte.find((rabatt: GvRabatt) => rabatt.zuschlagArt === rabattArt);
    return gvRabatt ? gvRabatt.prozent : 0;
  } else {
    return 0;
  }
}

export function hasGvPersonTarife(gvPerson: GvPerson) {
  return gvPerson && gvPerson.gvTarifData && gvPerson.gvTarifData.tarife && gvPerson.gvTarifData.tarife.length > 0;
}

export function hasGvAngebotTarife(gvPersonenData: GvPersonenData): boolean {
  return !!gvPersonenData.personen.find(hasGvPersonTarife);
}

export function hasGvTarifErrorMessages(gvTarif: GvTarif): boolean {
  if (gvTarif && gvTarif.berechnungMessages && gvTarif.berechnungMessages.berechnungMessage) {
    return !!gvTarif.berechnungMessages.berechnungMessage.find((msg) => msg.status === GvBerechnungMessageState.ERROR);
  } else {
    return false;
  }
}

export function hasGvAngebotTarifeAnyWarnings(gvPersonenData: GvPersonenData): boolean {
  return gvPersonenData.personen.some(hasGvPersonTarifeAnyWarnings);
}

export function hasGvPersonTarifeAnyWarnings(gvPerson: GvPerson): boolean {
  return (
    hasGvPersonTarifeMessages(gvPerson, hasGvTarifWarningMessages) ||
    hasGvPersonTarifeMessages(gvPerson, hasGvTarifKombinationWarningMessages) ||
    hasGvPersonTarifeMessages(gvPerson, hasGvTarifWeitereTarifeWarningMessages)
  );
}

export function hasGvPersonTarifeErrors(gvPerson: GvPerson): boolean {
  return hasGvPersonTarifeMessages(gvPerson, hasGvTarifErrorMessages);
}

export function hasGvPersonTarifeMessages(gvPerson: GvPerson, func: (gvTarif: GvTarif) => boolean): boolean {
  return gvPerson && gvPerson.gvTarifData && gvPerson.gvTarifData.tarife && gvPerson.gvTarifData.tarife.some(func);
}

export function hasGvAngebotTarifeErrors(gvPersonenData: GvPersonenData): boolean {
  return gvPersonenData.personen.some(hasGvPersonTarifeErrors);
}

export function hasGvTarifKombinationWarningMessages(tarif: GvTarif): boolean {
  if (tarif?.berechnungMessages?.berechnungMessage) {
    return !!tarif.berechnungMessages.berechnungMessage.some(
      (msg) => msg.status === GvBerechnungMessageState.NICHT_KOMBINIERBAR
    );
  } else {
    return false;
  }
}

export function hasGvTarifWeitereTarifeWarningMessages(tarif: GvTarif): boolean {
  if (tarif?.berechnungMessages?.berechnungMessage) {
    return !!tarif.berechnungMessages.berechnungMessage.some(
      (msg) => msg.status === GvBerechnungMessageState.WEITERE_TARIFE_ERFORDERLICH
    );
  } else {
    return false;
  }
}

export function hasGvTarifWarningMessages(gvTarif: GvTarif): boolean {
  if (gvTarif && gvTarif.berechnungMessages && gvTarif.berechnungMessages.berechnungMessage) {
    return !!gvTarif.berechnungMessages.berechnungMessage.some(
      (msg) => msg.status === GvBerechnungMessageState.WARNING
    );
  } else {
    return false;
  }
}

export function hasGvAngebotAenderungen(gvAngebotData: GvAngebotData): boolean {
  let angebotsGeschaeftsfaelle = gvAngebotData.ausgefuehrteGeschaeftsfaelle || [];

  if (gvAngebotData.aktenTrennung === 'ORIGIN') {
    angebotsGeschaeftsfaelle = angebotsGeschaeftsfaelle.filter((gf) => gf.id !== 'K0012'); // TODO: move GvGFall an use it (circular dep)
  }

  if (angebotsGeschaeftsfaelle.length > 0) {
    return true;
  } else {
    for (const person of gvAngebotData.gvPersonenData.personen) {
      if (person.ausgefuehrteGeschaeftsfaelle?.length) {
        return true;
      } else {
        if (person.gvTarifData?.tarife) {
          for (const tarif of person.gvTarifData.tarife) {
            if (tarif.ausgefuehrteGeschaeftsfaelle?.length) {
              return true;
            }
          }
        }
      }
    }
  }
  return false;
}

export function getGvTarife(personId: string, angebote: GvAngebotData[]): GvTarif[] {
  try {
    const tarife: GvTarif[] = angebote
      .map((angebot: GvAngebotData) =>
        angebot.gvPersonenData.personen
          .filter((person: GvPerson) => person.id === personId && person.gvTarifData && person.gvTarifData.tarife)
          .map((person: GvPerson) => person.gvTarifData.tarife)
      )
      .flat(Infinity) as GvTarif[];
    return tarife;
  } catch (error) {
    console.error(error);
  }

  return [];
}

export function isGvTarifValid(gvTarif: GvTarif): IValidationResult {
  if (hasGvTarifErrorMessages(gvTarif)) {
    return createValidationResult('gvTarif', null, 'Fehler sind vorhanden');
  }

  return null;
}
